import { useSelector } from "react-redux";
import { searchFormSelector, ISearchForm, IFlight } from "redux/searchForm";
import { initialSearchForm } from "redux/searchForm/initialState";
import { initialFlightObj } from "redux/filters/initialState";
import { isValidMetaSearchQuery } from "../validation/validateMetaRequest";

/**
 * Shared
 */
import {
  ICabinClassMap,
  ICabinType,
  ITripType,
  cabinClassMap,
  deepCloneObject,
} from "@thekilroygroup/shared-ui";

/**
 * Third party libraries
 */
import { addDays } from "date-fns";

export interface IQueryParams {
  searchQuery: ISearchForm;
  fareId: string | null;
}

const useQueryParams: any = () => {
  const searchForm = useSelector(searchFormSelector);

  const decodedUrl = window.location.href
    .replace(/\\u003d/g, "=")
    .replace(/\\u0026/g, "&");

  const searchParams = new URLSearchParams(decodedUrl.split("?")[1]);

  const parseParamsToQueryObj = () => {
    let searchQuery: ISearchForm = deepCloneObject(searchForm);
    let metaSearchQuery = deepCloneObject(initialSearchForm);

    if (searchParams.get("gdssource")) {
      searchQuery.gdssource = searchParams.get("gdssource") === "true";
    }
    if (searchParams.get("timestampInSeconds")) {
      searchQuery.timestampInSeconds = Number(
        searchParams.get("timestampInSeconds"),
      );
      metaSearchQuery.timestampInSeconds = Number(
        searchParams.get("timestampInSeconds"),
      );
    }
    if (searchParams.get("hmac")) {
      searchQuery.hmac = searchParams.get("hmac");
      metaSearchQuery.hmac = searchParams.get("hmac");
    }
    if (searchParams.get("utm_source")) {
      searchQuery.utm_source = searchParams.get("utm_source");
      metaSearchQuery.utm_source = searchParams.get("utm_source");
    }
    if (searchParams.get("utm_campaign")) {
      searchQuery.utm_campaign = searchParams.get("utm_campaign");
      metaSearchQuery.utm_campaign = searchParams.get("utm_campaign");
    }
    if (searchParams.get("utm_medium")) {
      searchQuery.utm_medium = searchParams.get("utm_medium");
      metaSearchQuery.utm_medium = searchParams.get("utm_medium");
    }
    if (searchParams.get("utm_content")) {
      searchQuery.utm_content = searchParams.get("utm_content");
      metaSearchQuery.utm_content = searchParams.get("utm_content");
    }
    if (searchParams.get("mktPortal")) {
      searchQuery.mktPortal = searchParams.get("mktPortal");
      metaSearchQuery.mktPortal = searchParams.get("mktPortal");
    }
    if (searchParams.get("totalPrice")) {
      searchQuery.totalPrice = Number(searchParams.get("totalPrice"));
      metaSearchQuery.totalPrice = Number(searchParams.get("totalPrice"));
    }
    if (searchParams.get("adultPrice")) {
      searchQuery.adultPrice = Number(searchParams.get("adultPrice"));
      metaSearchQuery.adultPrice = Number(searchParams.get("adultPrice"));
    }
    if (searchParams.get("childPrice")) {
      searchQuery.childPrice = Number(searchParams.get("childPrice"));
      metaSearchQuery.childPrice = Number(searchParams.get("childPrice"));
    }
    if (searchParams.get("infantPrice")) {
      searchQuery.infantPrice = Number(searchParams.get("infantPrice"));
      metaSearchQuery.infantPrice = Number(searchParams.get("infantPrice"));
    }

    if (searchParams.get("skyscanner_redirectid")) {
      searchQuery.clickId = searchParams.get("skyscanner_redirectid");
      metaSearchQuery.clickId = searchParams.get("skyscanner_redirectid");
    } else if (searchParams.get("gclid")) {
      searchQuery.clickId = searchParams.get("gclid");
      metaSearchQuery.clickId = searchParams.get("gclid");
    } else if (searchParams.get("clickId")) {
      searchQuery.clickId = searchParams.get("clickId");
      metaSearchQuery.clickId = searchParams.get("clickId");
    }

    if (searchParams.get("gclsrc")) {
      metaSearchQuery.gclsrc = searchParams.get("gclsrc");
    }

    if (searchParams.get("fareId")) {
      searchQuery.fareId = searchParams.get("fareId");
      metaSearchQuery.fareId = searchParams.get("fareId");
    }

    if (searchParams.get("adults")) {
      searchQuery.passengers.adults = Number(searchParams.get("adults"));
    }

    if (searchParams.get("children")) {
      searchQuery.passengers.children = Number(searchParams.get("children"));
    }

    if (searchParams.get("infants")) {
      searchQuery.passengers.infants = Number(searchParams.get("infants"));
    }

    if (searchParams.get("cabinClass")) {
      if (
        cabinClassMap.hasOwnProperty(
          searchParams?.get("cabinClass") as keyof ICabinClassMap,
        )
      ) {
        searchQuery.cabinClass =
          cabinClassMap[searchParams.get("cabinClass") as keyof ICabinClassMap];
      } else {
        searchQuery.cabinClass = searchParams
          .get("cabinClass")
          ?.toUpperCase() as ICabinType;
      }
    }

    if (searchParams.get("airlines") && searchParams.get("airlines") !== null) {
      // Make sure to handle both "comma" and url encoded comma "%2C"
      const params = searchParams.get("airline");
      let airlines =
        params !== null
          ? params.split(",").join("$").split("%2C").join("$").split("$")
          : "";
      if (typeof airlines === "object") searchQuery.airlines = airlines;
    }

    if (searchParams.get("flexibleSearch")) {
      searchQuery.flexibleSearch = Number(searchParams.get("flexibleSearch"));
    } else {
      searchQuery.flexibleSearch = 0;
    }

    if (searchParams.get("tripType")) {
      const mappedValue = flightTypeMap[
        searchParams.get("tripType") as keyof IFlightTypeMap
      ] as ITripType;
      searchQuery.tripType = mappedValue || searchParams.get("tripType");
    }

    if (
      searchQuery.tripType === "ONE_WAY" ||
      searchQuery.tripType === "RETURN"
    ) {
      searchQuery.flights.length = 1;

      if (searchParams.get("outFrom")) {
        searchQuery.flights[0].departure = searchParams.get("outFrom");
      }

      if (searchParams.get("outTo")) {
        searchQuery.flights[0].arrival = searchParams.get("outTo");
      }

      if (searchParams.get("outDate")) {
        searchQuery.flights[0].departureDate =
          searchParams.get("outDate")?.replaceAll("-", ".") || null;
      } else if (searchParams.get("outDate")) {
        searchQuery.flights[0].departureDate =
          searchParams.get("outDate")?.replaceAll("-", ".") || null;
      }

      if (searchQuery.tripType === "RETURN") {
        searchQuery.flights[1] = { ...initialFlightObj };

        if (searchParams.get("outTo")) {
          searchQuery.flights[1].departure = searchParams.get("outTo");
        }

        if (searchParams.get("outFrom")) {
          searchQuery.flights[1].arrival = searchParams.get("outFrom");
        }

        if (searchParams.get("retDate")) {
          searchQuery.flights[1].departureDate =
            searchParams.get("retDate")?.replaceAll("-", ".") || null;
        } else if (searchParams.get("retDate")) {
          searchQuery.flights[1].departureDate =
            searchParams.get("retDate")?.replaceAll("-", ".") || null;
        }
      }
    } else {
      searchQuery.tripType = "MULTI_DEST";
      searchQuery.flights.length = 1;
      searchQuery.flights[0] = deepCloneObject(searchForm.flights[0]);

      if (
        searchParams.get(`leg1From`) ||
        searchParams.get(`leg1To`) ||
        searchParams.get(`leg1Date`)
      ) {
        if (searchParams.get("leg1From")) {
          searchQuery.flights[0].departure = searchParams.get("leg1From");
        }

        if (searchParams.get("leg1To")) {
          searchQuery.flights[0].arrival = searchParams.get("leg1To");
        }

        if (searchParams.get("leg1Date")) {
          searchQuery.flights[0].departureDate = searchParams.get("leg1Date");
        }
      }

      for (let i = 2; i <= 10; i++) {
        if (
          searchParams.get(`leg${i}From`) ||
          searchParams.get(`leg${i}To`) ||
          searchParams.get(`leg${i}Date`)
        ) {
          let flight: IFlight = deepCloneObject(initialFlightObj);

          if (searchParams.get(`leg${i}From`)) {
            flight.departure = searchParams.get(`leg${i}From`);
          }

          if (searchParams.get(`leg${i}To`)) {
            flight.arrival = searchParams.get(`leg${i}To`);
          }

          if (searchParams.get(`leg${i}Date`)) {
            flight.departureDate =
              searchParams.get(`leg${i}Date`)?.replaceAll("-", ".") || null;
          }

          searchQuery.flights.push(flight);
        }
      }
    }
    if (searchParams.get("fareId")) {
      searchQuery.fareId = searchParams.get("fareId");
    }

    const fareId = searchParams.get("fareId")
      ? searchParams.get("fareId")
      : null;

    if (isValidMetaSearchQuery(metaSearchQuery)) {
      searchQuery.metaStorageTimeout = addDays(new Date(), 30);
    }
    return { searchQuery: searchQuery, fareId: fareId };
  };
  const nextSearchQuery = parseParamsToQueryObj();
  return nextSearchQuery as IQueryParams;
};
export default useQueryParams;

const flightTypeMap: IFlightTypeMap = {
  oneway: "ONE_WAY",
  return: "RETURN",
  multidest: "MULTI_DEST",
};
interface IFlightTypeMap {
  oneway: string;
  return: string;
  multidest: string;
}
